import { TableContainer, Paper, makeStyles } from '@material-ui/core'
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid'
import React, { useEffect, useReducer, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useProjectsQuery } from '../../generated/graphql'
import { ProjectSearchFormValues } from './ProjectSearchForm'
import { getDataGridLocale } from '../../util/data-grid-util'
import { ProjectListGridToolbar } from './ProjectListGridToolbar'
import { ProjectListGridFooter } from './ProjectListGridFooter'
import {
  ProjectListKeys,
  useProjectListData,
} from './util/use-project-list-data'
import { createProjectListFilter } from './util/project-util'

const DEFAULT_PAGE_SIZE = 8
const PAGE_SIZE_OPTIONS = [8, 25, 100]
const TABLE_WIDTH = 1300

const useStyles = makeStyles({
  gridContainer: {
    flexGrow: 1,
    width: '100%',
    maxWidth: TABLE_WIDTH,
    height: '620px',
  },
})

const useDataGridStyles = makeStyles(
  () => ({
    row: {
      cursor: 'pointer',
    },
  }),
  { name: 'MuiDataGrid' },
)

export function ProjectList(): React.ReactElement {
  const history = useHistory()
  const [keyword, setKeyword] = useState<string | undefined>(undefined)
  const [createdAfter, setCreatedAfter] = useState<Date | undefined>(undefined)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const { data, loading, refetch } = useProjectsQuery({
    variables: createProjectListFilter(pageSize),
  })
  const [offset, setOffset] = useState(0)
  const [rowCount, setRowCount] = useState(0)

  // The toolbar handles its internal filter state and only passes the internal state to the parent when the user clicks find
  // If the user does not click find we need to update the toolbar internal filter state after other user interaction such as CSV export
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  const classes = useStyles()
  useDataGridStyles()

  const { keyTranslations, mapProjectToListData } = useProjectListData()

  useEffect(() => {
    if (data && data.projects.totalCount !== rowCount) {
      setRowCount(data?.projects.totalCount ?? 0)
    }
  }, [data, rowCount])

  useEffect(() => {
    void refetch(
      createProjectListFilter(pageSize, offset, keyword, createdAfter),
    )
  }, [pageSize, offset, keyword, createdAfter, refetch])

  const createColumn = (
    field: ProjectListKeys,
    width: number,
    flex?: number,
    hide = false,
  ): GridColDef => ({
    field,
    headerName: keyTranslations[field],
    minWidth: width,
    flex,
    sortable: false,
    hide,
  })

  const columns: GridColDef[] = [
    createColumn(ProjectListKeys.ProjectId, 150),
    createColumn(ProjectListKeys.Name, 200),
    createColumn(ProjectListKeys.Area, 120),
    createColumn(ProjectListKeys.Updated, 400),
    createColumn(ProjectListKeys.Created, 400, 1),
    createColumn(ProjectListKeys.LicensePaid, 0, undefined, true),
  ]

  const rows = data?.projects.items.map(mapProjectToListData) ?? []

  const handleRowClick = (row: GridRowParams) => {
    history.push(`/projects/${row.row.id as string}`)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    setOffset(newPage * pageSize)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    if (offset !== 0) {
      setOffset(0)
    }
    if (page !== 0) {
      setPage(0)
    }
    setPageSize(newPageSize)
  }

  const handleFilterChange = ({
    keyword: newKeyword,
    createdAfter: newCreatedAfter,
  }: ProjectSearchFormValues) => {
    if (newKeyword !== keyword) {
      setKeyword(newKeyword)
    }
    if (newCreatedAfter !== createdAfter) {
      setCreatedAfter(newCreatedAfter ?? undefined)
    }
  }

  const searchFormDefaultValues = {
    keyword,
    createdAfter,
  }

  return (
    <TableContainer component={Paper} className={classes.gridContainer}>
      <DataGrid
        columns={columns}
        rows={rows}
        pagination
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        rowCount={rowCount}
        paginationMode="server"
        localeText={getDataGridLocale(pageSize)}
        page={page}
        onPageChange={handlePageChange}
        loading={loading}
        disableColumnMenu
        onRowClick={handleRowClick}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        components={{
          Toolbar: () =>
            ProjectListGridToolbar({
              searchFormDefaultValues,
              onSearchFormSubmit: handleFilterChange,
              disabled: loading,
            }),
          Footer: () =>
            ProjectListGridFooter({
              ...searchFormDefaultValues,
              onExport: forceUpdate,
            }),
        }}
      />
    </TableContainer>
  )
}
